import { FunctionComponent, useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';

import { useClientData } from '@/providers/ClientData.provider';
import { PaymentFields, PaymentFieldDefault } from '@/hooks/useFields/TransactionCreationFields/types';
import SinglePaymentForm from './SinglePaymentForm';

import usePostBulkPayoutRequest from '@/hooks/useApi/usePostBulkPayoutRequest';
import usePostEftWithdraw from '@/hooks/useApi/usePostEftWithdraw';
import usePostIQ11Tokenize from '@/hooks/useApi/usePostIQ11Tokenize';
import usePostScheduledEftWithdraw from '@/hooks/useApi/usePostScheduledEftWithdraw';
import usePostRtrWithdraw from '@/hooks/useApi/usePostRtrWithdraw';
import usePostACHWithdraw from '@/hooks/useApi/usePostACHWithdraw';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import usePostRtpWithdraw from '@/hooks/useApi/usePostRtpWithdraw';
import usePostFedwireWithdraw from '@/hooks/useApi/usePostFedwireWithdraw';
import usePostACHWithdrawSchedule from '@/hooks/useApi/usePostACHWithdrawSchedule';
import useVenmo from '@/hooks/useApi/useVenmo';
import usePayPal from '@/hooks/useApi/usePayPal';
import useDebitCard from '@/hooks/useApi/useDebitCard';
import AvailableBalance from '@/components/UI/AvailableBalance/AvailableBalance';
import { Box, IsTodayDate, UtilButton, withStyles, allCardBrands, SupportedCurrencies } from 'vopay';
import { Title } from 'UI';
import useContacts from '@/hooks/useApi/useContacts';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import { useTranslation } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import usePaymentMethods from '@/hooks/usePaymentMethods';
import { format } from 'date-fns';
import useUploadDocument from '@/hooks/useApi/useUploadDocument';
import { useHandlePaymentMethodChange } from '@/hooks/useFields/TransactionCreationFields';
import useFetchContactPaymentMethod from '@/hooks/useApi/useFetchContactPaymentMethod';
import ElinxConnectWrapper, { ElinxConnectRef } from '@/components/ElinxConnect/ElinxConnectWrapper';
import styles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';
import { useDataDog } from '@/providers/Datadog.provider';

const classes = withStyles(styles);

type TokenProps = {
  token: string;
  contact: string;
};

const SinglePaymentContainer: FunctionComponent = () => {
  const { state } = useLocation<TokenProps>();
  const history = useHistory();
  const elinxConnectRef = useRef<ElinxConnectRef>(null);
  const { setOpenModule } = useOpenModule();
  const { setSubNavActiveModule } = useNavMenu();
  const { logMessage } = useDataDog();
  const handlePaymentMethodChange = useHandlePaymentMethodChange();

  const contact = state?.contact || '';
  const token = state?.token || '';

  const [availableBalance, setAvailableBalance] = useState<string>('');

  const {
    response: { clientAccountsEnabled, Country, AccountID, AccountName, LoginUserName },
  } = useClientData();
  const [currency, setCurrency] = useState<SupportedCurrencies>(Country === 'US' ? 'USD' : 'CAD');
  const { t } = useTranslation('translations', { keyPrefix: 'SinglePayment' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<Partial<PaymentFieldDefault> | null>(null);
  const { fetchPaymentMethods } = useFetchContactPaymentMethod();
  const [, setPending] = useDataFetchIndicator();
  const { uploadDocument } = useUploadDocument();

  useEffect(() => {
    if (!contact || !token) {
      setSelectedPaymentMethod({});
      return;
    }

    const fetchPaymentMethod = async () => {
      try {
        setPending(true);
        const paymentMethodsResponse = await fetchPaymentMethods(state.contact);
        if (!paymentMethodsResponse.Success) {
          console.error(paymentMethodsResponse.ErrorMessage);
          throw new Error(paymentMethodsResponse.ErrorMessage);
        }
        const paymentMethod = paymentMethodsResponse.PaymentMethods.find((method) => method.Token === token);
        if (!paymentMethod) {
          console.error('Payment method not found for token:', token);
          throw new Error('Payment method not found for token:' + token);
        }
        const paymentMethodChangeValues = await handlePaymentMethodChange({
          Token: token,
          PaymentMethod: paymentMethod.PaymentMethod,
          Value: token,
          Contact: contact,
        });
        setSelectedPaymentMethod(paymentMethodChangeValues?.values || {});
      } catch (err) {
        if (!(err instanceof Error)) return;
        console.error('Error fetching payment method:', err);
        logMessage('Error fetching payment method', 'error', {
          feature: 'Single Payment',
          errorMessage: err.message,
          trace: err.stack,
        });
        alertDialog.error("Couldn't get payment method", err.message);
      } finally {
        setPending(false);
      }
    };

    fetchPaymentMethod();
  }, [state?.contact, state?.token]);
  const currentDate = useMemo(() => String(Date.now()), []);
  const withdrawDefaultValues: PaymentFieldDefault = {
    Currency: Country === 'US' ? 'USD' : 'CAD',
    PaymentMethod: token,
    BankingPaymentRail: Country === 'CA' ? 'eft' : 'ach',
    Contact: contact,
    PaymentSelectionMethod: '',
    Amount: '',
    ScheduleType: 'single',
    TakeFromAccountOn: currentDate,
    Frequency: 'monthly',
    SemiMonthlyDates: '',
    StartDate: currentDate,
    Ending: 'End Date',
    EndDate: '',
    Description: '',
    Message: '',
    Notes: '',
    ClientAccount: '',
    TransactionLabel: '',
    MessageForRecipient: '',
    EndingAfterPayments: '',
    IdentificationNumber: '',
    ClientReferenceNumber: '',
    FirstName: '',
    LastName: '',
    Address1: '',
    Address2: '',
    City: '',
    Province: '',
    Country,
    PostalCode: '',
    FinancialInstitutionNumber: '',
    BranchTransitNumber: '',
    ABARoutingNumber: '',
    AccountNumber: '',
    ContactType: 'individual',
    CompanyName: '',
    EtransferConnectionType: 'email',
    RecipientName: '',
    RecipientPhoneNumber: '',
    RecipientEmailAddress: '',
    Question: '',
    Answer: '',
    Nickname: '',
    ProvideOrRequestPaymentInformation: 'provide',
    AccountConnectionMethod: 'Manual',
    InvoiceNumber: '',
    SendEmail: false,
    SenderName: AccountName,
    SendNotification: true,
    SendReminder: true,
    ReminderFrequency: '7',
    NotificationMethod: 'email',
    CancellationPeriod: 14, //default is 14 days
    SecCode: 'PPD',
    AcceptedCardBrands: allCardBrands.join(','),
    CardTypeValidation: false,
    BankPhoneNumber: '',
    ...selectedPaymentMethod,
  };

  const [, , , postScheduledEftWithdraw] = usePostScheduledEftWithdraw({});
  const [, , , postEftWithdraw] = usePostEftWithdraw({});
  const [, , , postBulkPayoutResponse] = usePostBulkPayoutRequest({});
  const [, , , postRtrWithdraw] = usePostRtrWithdraw({});
  const [, , , postACHWithdraw] = usePostACHWithdraw({});
  const [, , , postACHWithdrawSchedule] = usePostACHWithdrawSchedule({});
  const [, , , postRtpWithdraw] = usePostRtpWithdraw({});
  const [, , , postFedwireWithdraw] = usePostFedwireWithdraw({});

  const { venmoWithdraw } = useVenmo();
  const { payPalWithdraw } = usePayPal();
  const { OCTPush } = useDebitCard();

  const { createContact, editContact, fetchContact } = useContacts();
  const { createClientAccount, editClientAccount, fetchClientAccount } = useClientAccounts();

  const { addInterac } = usePaymentMethods();

  const CUSTOMER_ID_PROP = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const alertDialog = useAlertDialog();

  const [, , addBankAccount] = usePostIQ11Tokenize({});

  const [, , , generateEmbedUrl] = usePostEmbedUrl({});
  const [, , , sendPaylink] = usePostSendPaylink({});

  const createPayment = async (values: PaymentFields) => {
    // TODO: Refactor into a factory function or strategy pattern to handle different PaymentSelectionMethods
    // This would reduce code duplication and make it easier to add new payment methods
    const base = {
      Amount: values.Amount || 0,
      PortalUser: AccountID || '',
      GLCode: values.GLCode || '',
      TransactionTypeCode: values.TransactionCode || '',
      ClientReferenceNumber: values.ClientReferenceNumber || '',
      TransactionLabel: values.TransactionLabel || '',
      [CUSTOMER_ID_PROP]: clientAccountsEnabled ? values.Contact : '',
    };
    const common = {
      FirstName: values.FirstName,
      LastName: values.LastName,
      CompanyName: values.CompanyName,
      PhoneNumber: values.RecipientPhoneNumber,
      Address1: values.Address1 || '',
      City: values.City || '',
      Province: values.Province || '',
      Country: Country || '',
      PostalCode: values.PostalCode || '',
      Token: clientAccountsEnabled ? '' : values.PaymentMethod,

      ...base,
    };
    try {
      setPending(true);

      if (values.PaymentSelectionMethod === 'bank') {
        const isScheduled =
          values.ScheduleType === 'recurring' || (values.TakeFromAccountOn && !IsTodayDate(values.TakeFromAccountOn));
        if (values.BankingPaymentRail === 'eft') {
          if (isScheduled) {
            const response = await postScheduledEftWithdraw({
              ...common,
              Frequency: values.ScheduleType || '',
              NameOfFrequency: values.Frequency || undefined,
              ScheduleStartDate: values.StartDate || values.TakeFromAccountOn || '',
              ScheduleEndDate: values.EndDate || '',
              EndingAfterPayments: values.EndingAfterPayments || '',
              Description: values.Description || '',
              SemiMonthlyFrequencyType: values.SemiMonthlyDates || '',
              EmailAddress: '',
              DOB: '',
              IPAddress: '',
              PlaidAccountID: '',
              PlaidPublicToken: '',
              PlaidAccessToken: '',
              PlaidProcessorToken: '',
              InveriteRequestGUID: '',
              IdempotencyKey: '',
              Notes: values.Notes,
            });
            if (!response.Success) {
              throw new Error(response.ErrorMessage);
            }

            const queryParams = new URLSearchParams({
              ContactID: values.Contact || '',
              token: values.PaymentMethod || '',
            });
            history.push(
              `/transaction-confirmation/scheduled/payment/EFTWithdrawal/${response.ScheduledTransactionID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
            );
          } else {
            const eftWithdrawResponse = await postEftWithdraw({
              ...common,
              Currency: values.Currency || '',
              Notes: values.Notes || '',
            });
            if (!eftWithdrawResponse.Success) {
              throw new Error(eftWithdrawResponse.ErrorMessage);
            }
            const queryParams = new URLSearchParams({
              ContactID: values.Contact || '',
              token: values.PaymentMethod || '',
            });
            history.push(
              `/transaction-confirmation/transaction/payment/EFTWithdrawal/${eftWithdrawResponse.TransactionID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
            );
          }
        }
        if (values.BankingPaymentRail === 'ach') {
          if (isScheduled) {
            const response = await postACHWithdrawSchedule({
              ...common,
              Frequency: values.ScheduleType || '',
              NameOfFrequency: values.Frequency || undefined,
              SemiMonthlyFrequencyType: values.SemiMonthlyDates || '',
              ScheduleStartDate: values.StartDate || values.TakeFromAccountOn || '',
              ScheduleEndDate: values.EndDate || '',
              PhoneNumber: values.RecipientPhoneNumber || common?.PhoneNumber || values.NewPhoneNumber || '',
              EndingAfterPayments: values.EndingAfterPayments || '',
              Description: values.Description || '',
              Address1: values.Address1 || '',
              City: values.City || '',
              State: values.Province || '',
              ZipCode: values.PostalCode || '',
              Currency: values.Currency || 'USD',
              IdentificationNumber: values.IdentificationNumber || '',
            });
            if (!response.Success) {
              throw new Error(response.ErrorMessage);
            }
            const queryParams = new URLSearchParams({
              ContactID: values.Contact || '',
              token: values.PaymentMethod || '',
            });
            history.push(
              `/transaction-confirmation/scheduled/payment/ACHWithdrawal/${response.ScheduledTransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
            );
          } else {
            const response = await postACHWithdraw({
              ...common,

              Currency: values.Currency || 'USD',
              PhoneNumber: values.RecipientPhoneNumber || common?.PhoneNumber || values.NewPhoneNumber || '',
              State: values.Province || '',
              ZipCode: values.PostalCode || '',
              IdentificationNumber: values.IdentificationNumber || '',
              EmailAddress: values.RecipientEmailAddress || '',
              SecCode: values.SecCode || 'PPD',
            });
            if (!response.Success) {
              throw new Error(response.ErrorMessage);
            }
            const queryParams = new URLSearchParams({
              ContactID: values.Contact || '',
              token: values.PaymentMethod || '',
            });
            history.push(
              `/transaction-confirmation/transaction/payment/ACHWithdrawal/${response.TransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
            );
          }
        }
        if (values.BankingPaymentRail === 'vopayinstant') {
          const response = await postRtrWithdraw({
            ...common,

            Notes: values.Notes || '',
            ClientReferenceNumber: values.ClientReferenceNumber || '',
          });
          if (!response.Success) {
            throw new Error(response.ErrorMessage);
          }
          history.push({
            pathname: `/transactions-history/details/VoPayInstantWithdraw/${response.TransactionID}/${
              values.Currency || ''
            }/`,
            state: {
              transactionId: response.TransactionID,
              activeModule: 'singlePayment',
              hasNewTransactionButton: true,
            },
          });
        }
        if (values.BankingPaymentRail === 'fedwire') {
          const response = await postFedwireWithdraw({
            Address1: values.Address1,
            City: values.City,
            State: values.Province,
            Country: 'US',
            ZipCode: values.PostalCode,
            FirstName: values.FirstName,
            LastName: values.LastName,
            CompanyName: values.CompanyName,
            Token: clientAccountsEnabled ? '' : values.PaymentMethod,

            ...base,
          });
          if (!response.Success) {
            throw new Error(response.ErrorMessage);
          }
          const queryParams = new URLSearchParams({
            ContactID: values.Contact || '',
            token: values.PaymentMethod || '',
          });
          history.push(
            `/transaction-confirmation/transaction/payment/Fedwire/${response.TransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
          );
        }
        if (values.BankingPaymentRail === 'rtp') {
          const response = await postRtpWithdraw({
            ...common,

            RecipientName: values.RecipientName,
          });
          if (!response.Success) {
            throw new Error(response.ErrorMessage);
          }
          const queryParams = new URLSearchParams({
            ContactID: values.Contact || '',
            token: values.PaymentMethod || '',
          });
          history.push(
            `/transaction-confirmation/transaction/payment/RTPWithdrawal/${response.TransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
          );

          setOpenModule('statements');
          setSubNavActiveModule('transactionsHistory');
        }
      } else if (values.PaymentSelectionMethod === 'paypal') {
        const response = await payPalWithdraw({
          PayPalToken: clientAccountsEnabled ? '' : values.PaymentMethod,

          Currency: values?.Currency || 'USD',
          Notes: values?.Notes || '',
          ...base,
        });
        if (!response.Success) {
          throw new Error(response.ErrorMessage);
        }
        const queryParams = new URLSearchParams({
          ContactID: values.Contact || '',
          token: values.PaymentMethod || '',
        });
        history.push(
          `/transaction-confirmation/transaction/payment/PayPalWithdrawal/${response.TransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
        );
      } else if (values.PaymentSelectionMethod === 'venmo') {
        const response = await venmoWithdraw({
          VenmoToken: clientAccountsEnabled ? '' : values.PaymentMethod,

          Notes: values?.Notes || '',
          ...base,
        });
        if (!response.Success) {
          throw new Error(response.ErrorMessage);
        }
        const queryParams = new URLSearchParams({
          ContactID: values.Contact || '',
          token: values.PaymentMethod || '',
        });
        history.push(
          `/transaction-confirmation/transaction/payment/VenmoWithdrawal/${response.TransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
        );
      } else if (values.PaymentSelectionMethod === 'debitcard') {
        const response = await OCTPush({
          Currency: values.Currency || '',
          CardToken: clientAccountsEnabled ? '' : values.PaymentMethod,
          Notes: values.Notes || '',
          ...base,
        });
        if (!response.Success) {
          throw new Error(response.ErrorMessage);
        }
        const queryParams = new URLSearchParams({
          ContactID: values.Contact || '',
          token: values.PaymentMethod || '',
        });
        history.push(
          `/transaction-confirmation/transaction/payment/OCTPush/${response.TransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
        );
      } else if (values.PaymentSelectionMethod === 'email') {
        const response = await postBulkPayoutResponse({
          Currency: values.Currency || '',
          EmailAddress:
            !clientAccountsEnabled && values.EtransferConnectionType === 'email'
              ? values.RecipientEmailAddress || ''
              : '',
          PhoneNumber:
            !clientAccountsEnabled && values.EtransferConnectionType === 'phone' ? values.RecipientPhoneNumber : '',
          RecipientName: values.RecipientName || '',
          Memo: values.Message || values.MessageForRecipient || '',

          Notes: values.Notes || '',

          Language: 'en',
          Question: values.Question || '',
          Answer: values.Answer || '',
          MessageForRecipient: values.Message || '',
          CancellationPeriod: values.CancellationPeriod,

          ...base,
        });
        if (!response.Success) {
          throw new Error(response.ErrorMessage);
        }
        const queryParams = new URLSearchParams({
          ContactID: values.Contact || '',
          token: values.PaymentMethod || '',
        });
        history.push(
          `/transaction-confirmation/transaction/payment/BulkPayout/${response.TransactionID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
        );
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      logMessage('Error creating payment', 'error', {
        feature: 'Single Payment',
        errorMessage: error instanceof Error ? error.message : String(error),
        trace: error instanceof Error ? error.stack : undefined,
        formValues: {
          values,
          base,
          common,
        },
      });
      alertDialog.error('Error', error instanceof Error ? error.message : String(error), {
        abortLabel: t('close'),
      });
    } finally {
      setPending(false);
    }
  };

  const handleSubmit = async (values: PaymentFields) => {
    const isNewContact = !values.Contact || values.Contact === 'new';
    const isNewPaymentMethod =
      isNewContact || !values.PaymentMethod || values.PaymentMethod === 'new' || values.PaymentMethod === 'request';
    const isRequest = values.ProvideOrRequestPaymentInformation === 'request';
    let contactClientAccountID = values.Contact;
    try {
      setPending(true);
      if (isNewContact) {
        if (clientAccountsEnabled) {
          const res = await createClientAccount(
            {
              FirstName: values.FirstName,
              LastName: values.LastName,
              EmailAddress: values.NewEmailAddress,
              PhoneNumber: values.NewPhoneNumber,
              BusinessName: values.CompanyName,
              Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
              City: values.City,
              Province: values.Province,
              PostalCode: values.PostalCode,
              Country: values.Country,
              Currency: Country === 'CA' ? 'CAD' : 'USD',
              IdentificationNumber: values.IdentificationNumber,
            },
            false,
          );
          values.RecipientName = values.CompanyName || `${values.FirstName} ${values.LastName}`;
          contactClientAccountID = res.clientAccountID;
        } else {
          const res = await createContact({
            FirstName: values.FirstName,
            LastName: values.LastName,
            //Default email and phone number are required for creating contact because some payment methods require them
            EmailAddress:
              values.RecipientEmailAddress || values.ReceiverEmailAddress || 'account@clientaccount.vopay.com',
            PhoneNumber: values.ReceiverPhoneNumber || values.RecipientPhoneNumber || '0000000000',
            BusinessName: values.CompanyName,
            Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
            City: values.City,
            Province: values.Province,
            PostalCode: values.PostalCode,
            Country: values.Country,
          });
          contactClientAccountID = res.contactID;
          values.RecipientName = values.CompanyName || `${values.FirstName} ${values.LastName}`;
        }
      } else if (contactClientAccountID) {
        // Update existing contact/client account with address if address fields are filled
        const hasAddressData =
          values.Address1 &&
          values.Address1.trim() !== '' &&
          values.City &&
          values.City.trim() !== '' &&
          values.Province &&
          values.Province.trim() !== '' &&
          values.PostalCode &&
          values.PostalCode.trim() !== '';

        let address1 = '';
        let city = '';
        let province = '';

        let postalCode = '';
        let phoneNumber = '';

        if (clientAccountsEnabled) {
          const account = await fetchClientAccount(contactClientAccountID);

          if (account) {
            address1 = account.Address1 || '';
            city = account.City || '';
            province = account.Province || '';

            postalCode = account.PostalCode || '';
            phoneNumber = account.PhoneNumber || '';
          }
        } else {
          const contact = await fetchContact(contactClientAccountID);
          if (contact) {
            address1 = contact.Address1 || '';
            city = contact.City || '';
            province = contact.Province || '';

            postalCode = contact.PostalCode || '';
            phoneNumber = contact.PhoneNumber || '';
          }
        }
        const isAddressChanged =
          values.Address1 + (values.Address2 ? `, ${values.Address2}` : '') !== address1 ||
          values.City !== city ||
          values.Province !== province ||
          values.PostalCode !== postalCode;

        const hasPhoneData =
          values.RecipientPhoneNumber &&
          values.RecipientPhoneNumber.trim() !== '' &&
          values.RecipientPhoneNumber !== '0000000000';
        const isPhoneChanged = hasPhoneData && values.RecipientPhoneNumber !== phoneNumber;

        if ((hasAddressData && isAddressChanged) || isPhoneChanged) {
          if (clientAccountsEnabled) {
            await editClientAccount({
              ClientAccountID: contactClientAccountID,
              ...(hasAddressData &&
                isAddressChanged && {
                  Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
                  City: values.City,
                  Province: values.Province,
                  PostalCode: values.PostalCode,
                  Country: values.Country,
                }),
              ...(isPhoneChanged && {
                PhoneNumber: values.RecipientPhoneNumber,
              }),
            });
          } else {
            await editContact({
              ContactID: contactClientAccountID,
              ...(hasAddressData &&
                isAddressChanged && {
                  Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
                  City: values.City,
                  Province: values.Province,
                  PostalCode: values.PostalCode,
                  Country: values.Country,
                }),
              ...(isPhoneChanged && {
                PhoneNumber: values.RecipientPhoneNumber,
              }),
            });
          }
        }
      }
      if (isNewPaymentMethod) {
        if (isRequest) {
          //TODO: modify to handle payments
          handleRequestPaymentMethod(values, contactClientAccountID || ''); // Pass the account
        } else {
          const token = await handleAddPaymentMethod(values, contactClientAccountID || ''); // Pass the account ID or contact client account ID
          if (!token) return;
          createPayment({
            ...values,
            Contact: contactClientAccountID || '',
            PaymentMethod: token || '',
          });
        }
      } else {
        createPayment({
          ...values,
          Contact: contactClientAccountID || '',
        });
      }
    } catch (error) {
      console.error('Error handling submit:', error);
      logMessage('Error handling submit', 'error', {
        feature: 'Single Payment',
        errorMessage: error instanceof Error ? error.message : String(error),
        trace: error instanceof Error ? error.stack : undefined,
        formValues: values,
      });
      alertDialog.error('Error', error instanceof Error ? error.message : String(error), {
        abortLabel: t('close'),
      });
    } finally {
      setPending(false);
    }
  };
  async function handleAddPaymentMethod(values: PaymentFields, accountID: string): Promise<string | undefined> {
    if (values.PaymentSelectionMethod === 'bank') {
      return await handleAddBankAccount(values, accountID);
    } else if (values.PaymentSelectionMethod === 'email') {
      return await handleAddInterac(values, accountID);
    } else if (values.PaymentSelectionMethod === 'paypal') {
      return await elinxConnectRef.current?.open(
        {
          PaymentSelectionMethod: 'paypal',
          ClientControlled: clientAccountsEnabled,
        },
        accountID,
      );
    } else if (values.PaymentSelectionMethod === 'venmo') {
      return await elinxConnectRef.current?.open(
        {
          PaymentSelectionMethod: 'venmo',
          ClientControlled: clientAccountsEnabled,
        },
        accountID,
      );
    } else if (values.PaymentSelectionMethod === 'debitcard') {
      return await elinxConnectRef.current?.open(
        {
          PaymentSelectionMethod: 'debitcard',
          ClientControlled: clientAccountsEnabled,
        },
        accountID,
      );
    }
    return undefined;
  }

  async function handleAddInterac(values: PaymentFields, accountID: string): Promise<string | undefined> {
    try {
      setPending(true);
      const res = await addInterac({
        accountID,
        recipientName: values.RecipientName,
        email: values.RecipientEmailAddress,
        phone: values.RecipientPhoneNumber,
        question: values.Question,
        answer: values.Answer,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      return res.Token;
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        console.error('Error adding interac:', err);
        logMessage('Error adding interac', 'error', {
          feature: 'Single Payment',
          errorMessage: err instanceof Error ? err.message : String(err),
          trace: err instanceof Error ? err.stack : undefined,
          formValues: {
            accountID,
            ...values,
          },
        });
        alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    } finally {
      setPending(false);
    }
  }
  async function handleAddBankAccount(values: PaymentFields, accountID: string): Promise<string | undefined> {
    try {
      setPending(true);
      const accountHolderName = values.CompanyName ? values.CompanyName : `${values.FirstName} ${values.LastName}`;
      const bankRes = await addBankAccount({
        PortalUser: LoginUserName,
        //TODO add company name some how
        AccountHolderName: accountHolderName,
        Currency: values.Currency || (Country === 'CA' ? 'CAD' : 'USD'),
        AccountNumber: values.AccountNumber,
        FinancialInstitutionNumber: values.FinancialInstitutionNumber,
        BranchTransitNumber: values.BranchTransitNumber,
        ABARoutingNumber: values.ABARoutingNumber, // for US account
        ClientControlled: clientAccountsEnabled,
        [CUSTOMER_ID_PROP]: accountID,
        Address1: values.Address1,
        City: values.City,
        Province: values.Province,
        PostalCode: values.PostalCode,
        PhoneNumber: values.RecipientPhoneNumber || values.NewPhoneNumber,
        Country: Country,
      });
      if (!bankRes.Success) {
        throw new Error(bankRes.ErrorMessage);
      }

      return bankRes.Token;
    } catch (err) {
      logMessage('Error adding bank account', 'error', {
        feature: 'Single Payment',
        errorMessage: err instanceof Error ? err.message : String(err),
        trace: err instanceof Error ? err.stack : undefined,
        formValues: {
          accountID,
          ...values,
        },
      });
      console.error(err);
      if (err instanceof Error) {
        await alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    } finally {
      setPending(false);
    }
  }

  async function handleRequestPaymentMethod(values: PaymentFields, accountID: string) {
    let documentID = '';
    try {
      setPending(true);

      const embedUrlRes = await generateEmbedUrl({
        RedirectURL: 'https://request-dev.vopay.com/',
        Language: 'en',
        RedirectMethod: 'JavascriptMessage',
        PaymentSelectionMethod: values.PaymentSelectionMethod,
        AccountSelectionMethod: values.AccountSelectionMethod,
        ClientControlled: clientAccountsEnabled,
        [CUSTOMER_ID_PROP]: accountID,
        ClientReferenceNumber: '',
        TermsAndConditions: '',
        Country: Country,
        AcceptedCardBrands: values.AcceptedCardBrands,
        CardTypeValidation: values.CardTypeValidation,
      });

      if (!embedUrlRes.Success) {
        throw new Error(embedUrlRes.ErrorMessage);
      }

      if (values.InvoiceFileName && values.InvoiceFile) {
        const documentUploadRes = await uploadDocument({
          DocumentContent: values.InvoiceFile,
          DocumentType: 'invoice',
          DocumentName: values.InvoiceFileName,
        });
        if (!documentUploadRes.Success) {
          throw new Error(documentUploadRes.ErrorMessage);
        }
        documentID = documentUploadRes.DocumentID;
      }
      const receiverName = values.CompanyName ? values.CompanyName : `${values.FirstName} ${values.LastName}`;
      const now = new Date();
      const isToday =
        format(now, 'yyyy-MM-dd') === values.StartDate || values.StartDate === undefined || values.StartDate === '';
      const sendPaylinkRes = await sendPaylink({
        Amount: values.Amount || 0,
        Currency: values.Currency || (Country === 'CA' ? 'CAD' : 'USD'),
        SenderName: values.SenderName || AccountName || '',
        ReceiverName: receiverName,
        ReceiverEmailAddress: values.ReceiverEmailAddress,
        ReceiverPhoneNumber: values.ReceiverPhoneNumber,
        IframeKey: embedUrlRes.IframeKey,
        [CUSTOMER_ID_PROP]: accountID,
        SendNotification: !!values.SendNotification,
        NotificationMethod: values.NotificationMethod,
        PaymentType: 'withdraw',
        InvoiceNumber: values.InvoiceNumber,
        Note: values.Notes,
        ClientReferenceNumber: values.ClientReferenceNumber,
        SendEmail: values.SendEmail,
        DocumentID: documentID,
        PaymentMethodDisplayOptions: values.PaymentMethodDisplayOptions,
        SemiMonthlyFrequencyType: !isToday ? values.SemiMonthlyDates : '',
        Frequency: !isToday ? values.ScheduleType : '',
        NameOfFrequency: !isToday ? values.Frequency : undefined,
        ScheduleStartDate: !isToday ? values.StartDate : '',
        ScheduleEndDate: !isToday ? values.EndDate : '',
        EndingAfterPayments: !isToday ? values.EndingAfterPayments : '',
        SendReminder: values.SendReminder,
        ReminderFrequency: values.ReminderFrequency,
        RecipientMessage: values.RecipientMessage,
      });
      if (!sendPaylinkRes.Success) {
        throw new Error(sendPaylinkRes.ErrorMessage);
      }

      // Navigate to eLinx confirmation page
      const notificationMethod = values.SendNotification
        ? values.NotificationMethod?.toLowerCase?.() || 'none'
        : 'none';
      const queryParams = new URLSearchParams({
        notificationMethod,
        ContactID: accountID,
        scheduled: (!isToday).toString(),
      });
      history.push(
        `/transaction-confirmation/elinx/payment/ePay/${sendPaylinkRes.ELinxRequestID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
      );
    } catch (err) {
      if (err instanceof Error) {
        console.error('Error requesting payment:', err);
        logMessage('Error  requesting payment', 'error', {
          feature: 'Single Payment',
          errorMessage: err instanceof Error ? err.message : String(err),
          trace: err instanceof Error ? err.stack : undefined,
          formValues: {
            documentID,
            accountID,
            ...values,
          },
        });
        alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className={classes('pageContainer')}>
      <div
        style={{
          position: 'sticky',
          top: '50px',
          zIndex: 10,
          backgroundColor: '#fbfbfb',
          maskImage: 'linear-gradient(to bottom, black 80%, transparent)',
          backdropFilter: 'blur(5px)',
          width: '100%',
        }}
      >
        <Box flx="row" jc="center">
          <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
            {state && (
              <UtilButton
                iconLeft="chevron_left"
                size="large"
                onClick={() => {
                  history.goBack();
                }}
              >
                {tGlobal('Buttons.back')}
              </UtilButton>
            )}
            <Title>
              <Box ai="center" gapSize="4px" jc="sb">
                {t('pageTitle')}
                <AvailableBalance availableBalance={availableBalance} currency={currency} />
              </Box>
            </Title>
          </Box>
        </Box>
      </div>
      <Box flx="row" jc="center">
        <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
          <Box flx="col" gap="md">
            {selectedPaymentMethod && (
              <SinglePaymentForm
                defaultValues={withdrawDefaultValues}
                onSubmit={handleSubmit}
                onAvailableBalanceChange={setAvailableBalance}
                onCurrencyChange={setCurrency}
              />
            )}
          </Box>
        </Box>
      </Box>

      <ElinxConnectWrapper ref={elinxConnectRef} />
    </div>
  );
};

export default SinglePaymentContainer;
